import { useRef, useContext, useState, useEffect } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import type { XYCoord } from 'dnd-core';
import ControlKind from '../Shared/controlKind';
import ControlState from '../Shared/controlState';
import Stack from '@mui/material/Stack';
import ControlEngine from '../ControlEngine/ControlEngine';
import {
  WorkflowEngineContext,
  WorkflowEngineActionKind
} from '../WorkflowEngine/workflowEngineReducer';
import { PreviewCard, DragTypes } from './';
import type { DraggableItem } from './';
import { JSONSchema7 } from 'json-schema';

interface DragProps {
  id: string;
  pageId: string;
  moveCard: (id: string, atIndex: number) => void;
  findCard: (id: string) => { index: number };
  createNewCard: (type: ControlKind, page: string, index: number) => void;
  control: ControlState;
  isReadOnlyView: boolean;
}

export default ({
  id,
  pageId,
  moveCard,
  findCard,
  createNewCard,
  control,
  isReadOnlyView
}: DragProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { dispatch } = useContext(WorkflowEngineContext);
  const [isOnTop, setIsOnTop] = useState<boolean>(true);
  const originalIndex = findCard(id).index;

  const checkPosition = (clientOffset: XYCoord | null) => {
    if (!ref.current) {
      return;
    }

    // Determine rectangle on screen
    const hoverBoundingRect = ref.current?.getBoundingClientRect();

    // Get vertical middle
    const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

    // Get pixels to the top
    const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

    // Draggin on the top portion
    if (hoverClientY < hoverMiddleY) {
      setIsOnTop(true);
      return;
    }

    // Draggin on the bottom portion
    if (hoverClientY > hoverMiddleY) {
      setIsOnTop(false);
      return;
    }
  };

  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type: DragTypes.CARD_CONTROL,
      item: { id, originalIndex, pageId, type: control.kind },
      canDrag: !isReadOnlyView && !control.building,
      end: (item, monitor) => {
        const { id: droppedId, originalIndex } = item;
        const didDrop = monitor.didDrop();
        if (!didDrop) {
          moveCard(droppedId, originalIndex);
        }
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging()
      })
    }),
    [id, originalIndex, moveCard, control, pageId]
  );

  const [{ isActive, itemDragged, itemType }, drop] = useDrop(
    () => ({
      accept: [DragTypes.CARD_CONTROL, DragTypes.PALETTE_CONTROL],
      hover(item: DraggableItem, monitor) {
        // Hover of cardControl in the same page
        if (
          monitor.getItemType() === DragTypes.CARD_CONTROL &&
          item.id !== id &&
          item.pageId === pageId
        ) {
          if (!ref.current) {
            return;
          }

          const { index: hoverIndex } = findCard(id);
          const { index: dragIndex } = findCard(item.id);

          if (dragIndex === hoverIndex) {
            return;
          }

          const hoverBoundingRect = ref.current?.getBoundingClientRect();
          const hoverMiddleY =
            (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
          const clientOffset = monitor.getClientOffset();
          const hoverClientY =
            (clientOffset as XYCoord).y - hoverBoundingRect.top;

          if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
            return;
          }

          if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
            return;
          }

          moveCard(item.id, hoverIndex);
        }

        // Hover from the palette to control the previewCard behaviour
        if (monitor.getItemType() === DragTypes.PALETTE_CONTROL) {
          checkPosition(monitor.getClientOffset());
        }
      },
      drop(item: DraggableItem, monitor) {
        checkPosition(monitor.getClientOffset());
        const positionIndex = isOnTop ? originalIndex : originalIndex + 1;
        if (monitor.getItemType() === DragTypes.PALETTE_CONTROL && item.type) {
          createNewCard(item.type, pageId, positionIndex);
        }
      },
      collect: (monitor) => ({
        isActive: monitor.canDrop() && monitor.isOver(),
        itemDragged: monitor.getItem(),
        itemType: monitor.getItemType()
      })
    }),
    [
      findCard,
      moveCard,
      createNewCard,
      control,
      checkPosition,
      id,
      pageId,
      isOnTop
    ]
  );

  const checkForStatus = () => {
    if (control.kind != ControlKind.SELECT) return;

    const statusData = (control.schema as JSONSchema7).properties;
    if (statusData && statusData.statusEnabled) {
      const statusValue = statusData.statusEnabled as JSONSchema7;
      if (statusValue.default)
        dispatch({
          type: WorkflowEngineActionKind.SET_STATUS_ENABLE,
          payload: true
        });
    }
  };

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
    checkForStatus();
  }, []);

  drag(drop(ref));
  return (
    <div ref={ref}>
      {isActive &&
        itemType === DragTypes.PALETTE_CONTROL &&
        isOnTop &&
        PreviewCard(itemDragged.type)}

      {isDragging && itemDragged.pageId === pageId ? (
        PreviewCard(itemDragged.type)
      ) : (
        <Stack>
          <ControlEngine control={control} />
        </Stack>
      )}

      {isActive &&
        itemType === DragTypes.PALETTE_CONTROL &&
        !isOnTop &&
        PreviewCard(itemDragged.type)}
    </div>
  );
};
